import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ClipboardCheck, 
  FileSearch, 
  Clock, 
  Eye, 
  ShieldCheck,
  Settings,
  Users,
  Headphones,
  Truck,
  Wrench,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { eldBenefits as defaultBenefits, whyChooseUs as defaultWhyChoose, heroContent as defaultHero, ctaContent as defaultCta } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const benefitIcons = [
  ClipboardCheck,
  FileSearch,
  Clock,
  Eye,
  ShieldCheck
];

const whyIcons = [
  Settings,
  Users,
  Headphones,
  Truck,
  Wrench
];

const HomePage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        setContent(response.data);
      } catch (error) {
        console.error('Error fetching content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  // Use database content or fallback to mock data
  const heroTitle = content?.hero_title || defaultHero.title;
  const heroSubtitle = content?.hero_subtitle || defaultHero.subtitle;
  const primaryCTA = content?.hero_primary_cta || defaultHero.primaryCTA;
  const secondaryCTA = content?.hero_secondary_cta || defaultHero.secondaryCTA;
  const ctaText = content?.cta_text || defaultCta.text;
  const ctaButtonText = content?.cta_button_text || defaultCta.buttonText;
  const eldBenefits = (content?.eld_benefits?.length > 0) ? content.eld_benefits : defaultBenefits;
  const whyChooseUs = (content?.why_choose_us?.length > 0) ? content.why_choose_us : defaultWhyChoose;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-navy min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        {/* Gradient Overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-navy to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 pt-48">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              <span className="text-red-500">United ELD LLC</span>
              <br />
              {heroTitle.replace('United ELD LLC — ', '').replace('United ELD LLC —', '')}
            </h1>
            <p className="text-lg sm:text-xl text-white/70 leading-relaxed mb-10 max-w-2xl">
              {heroSubtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-6 text-lg w-full sm:w-auto transition-all duration-300 hover:scale-105">
                  {primaryCTA}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  variant="outline" 
                  className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold px-8 py-6 text-lg w-full sm:w-auto transition-all duration-300"
                >
                  {secondaryCTA}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What Our ELD Helps With Section */}
      <section className="bg-gray-light py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy mb-4">
              What Our ELD Helps With
            </h2>
            <div className="w-20 h-1 bg-red-600 mx-auto rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {eldBenefits.map((benefit, index) => {
              const Icon = benefitIcons[index % benefitIcons.length];
              return (
                <div 
                  key={benefit.id || index}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
                >
                  <div className="w-14 h-14 bg-red-600/10 rounded-xl flex items-center justify-center mb-5">
                    <Icon className="h-7 w-7 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-3">{benefit.title}</h3>
                  <p className="text-navy/60 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why United ELD LLC Section */}
      <section className="bg-navy py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why United ELD LLC
            </h2>
            <div className="w-20 h-1 bg-red-600 mx-auto rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {whyChooseUs.map((item, index) => {
              const Icon = whyIcons[index % whyIcons.length];
              return (
                <div 
                  key={item.id || index}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-red-600 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CheckCircle className="h-12 w-12 text-white/80 mx-auto mb-6" />
          <p className="text-xl sm:text-2xl text-white font-medium leading-relaxed mb-8">
            {ctaText}
          </p>
          <Link to="/contact">
            <Button 
              className="bg-white text-red-600 hover:bg-gray-100 font-bold px-10 py-6 text-lg transition-all duration-300 hover:scale-105 shadow-lg"
            >
              {ctaButtonText}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
